import { EventBus } from '@/core/EventBus'
import { Scheduler, Stage, type SystemContext, type SystemFn } from '@/core/ecs/Scheduler'
import { World } from '@/core/ecs/World'
import { Rng } from '@/core/Rng'
import { FIXED_DT } from '@/core/Time'
import { ARENAS } from '@/game/arenas'
import { ArenaResource, ArenaState } from '@/game/arenas/Arena'
import {
  DEFAULT_SETTINGS,
  EventsResource,
  MatchResource,
  RngResource,
  SettingsResource,
  createMatchState,
  type Settings,
} from '@/game/resources'
import { InputResource, consumePresses, createInputFrame } from '@/input/InputManager'
import { abilitySystem } from '@/systems/AbilitySystem'
import { aiSystem } from '@/systems/AiSystem'
import { arenaBoundsSystem } from '@/systems/ArenaBoundsSystem'
import { collisionSystem } from '@/systems/CollisionSystem'
import { fieldSystem } from '@/systems/FieldSystem'
import { hazardSystem } from '@/systems/HazardSystem'
import { inputApplySystem } from '@/systems/InputSystem'
import { movementSystem, transformSnapshotSystem } from '@/systems/MovementSystem'
import { createPickupSystem } from '@/systems/PickupSystem'
import { projectileSystem } from '@/systems/ProjectileSystem'
import { roundSystem } from '@/systems/RoundSystem'

export type SimulationSystem =
  | 'ai'
  | 'inputApply'
  | 'snapshot'
  | 'abilities'
  | 'projectiles'
  | 'fields'
  | 'hazards'
  | 'movement'
  | 'collision'
  | 'bounds'
  | 'pickups'
  | 'round'

export interface Simulation {
  readonly world: World
  readonly scheduler: Scheduler
  readonly events: EventBus
}

export interface SimulationOptions {
  /** Seed of the simulation's own RNG: hazards and pickups. */
  seed?: number
  settings?: Settings
  /**
   * Registers only these systems. For the physics probes, which measure one
   * mechanism with everything else switched off; a match never uses it.
   */
  only?: readonly SimulationSystem[]
}

/**
 * The one place the fixed-step simulation is assembled.
 *
 * The browser, simcheck and the game server all build their world here, so the
 * bot a player meets, the bot the balance was measured against and the bot on
 * the server are the same bot running in the same order. The AI and the input
 * apply sit inside the step: a bot decides on simulated time, not on the
 * refresh rate of whoever is watching. See docs/adr/0002-one-simulation-assembly.md.
 */
export function createSimulation(options: SimulationOptions = {}): Simulation {
  const world = new World()
  const scheduler = new Scheduler()
  const events = new EventBus()

  world.setResource(EventsResource, events)
  world.setResource(RngResource, new Rng(options.seed ?? 0x1234abcd))
  world.setResource(SettingsResource, options.settings ?? { ...DEFAULT_SETTINGS })
  world.setResource(MatchResource, createMatchState(ARENAS[0].id, 3))
  world.setResource(ArenaResource, new ArenaState(ARENAS[0]))
  world.setResource(InputResource, createInputFrame())

  const systems: Array<[SimulationSystem, SystemFn, number]> = [
    // The AI writes the slots it owns, then the frame is applied to the actors:
    // a bot plays through the same buffer and lockouts as a human.
    ['ai', aiSystem, -10],
    ['inputApply', inputApplySystem, -5],
    ['snapshot', transformSnapshotSystem, 0],
    ['abilities', abilitySystem, 10],
    ['projectiles', projectileSystem, 15],
    ['fields', fieldSystem, 20],
    ['hazards', hazardSystem, 30],
    ['movement', movementSystem, 40],
    ['collision', collisionSystem, 50],
    ['bounds', arenaBoundsSystem, 60],
    ['pickups', createPickupSystem(), 65],
    ['round', roundSystem, 70],
  ]
  for (const [name, fn, order] of systems) {
    if (!options.only || options.only.includes(name)) {
      scheduler.add(Stage.Simulation, name, fn, order)
    }
  }

  return { world, scheduler, events }
}

/**
 * Runs one fixed 1/60 s step.
 *
 * The step consumes the presses in the input frame, so each one is applied on
 * exactly one step however many a rendered frame runs (`consumePresses`).
 * Events are left for the caller to drain: the browser drains them once per
 * rendered frame, after presentation, and the server once per tick, after
 * copying them into the next snapshot.
 */
export function stepSimulation(sim: Simulation, elapsed: number): void {
  const ctx: SystemContext = { world: sim.world, dt: FIXED_DT, alpha: 0, elapsed }
  sim.scheduler.run(Stage.Simulation, ctx)
  sim.scheduler.run(Stage.PostSim, ctx)
  consumePresses(sim.world.resource(InputResource))
}
